import React, { useState, useRef, useEffect } from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalCloseButton,
    ModalBody,
    Box
} from '@chakra-ui/react';

function ImageModal({ imageSrc, isOpen, onClose, hideZoomControls = false }) {
    // State for zoom functionality
    const [zoomLevel, setZoomLevel] = useState(1); // 1 = 100%
    const minZoom = 1; // 100% - don't allow zoom out below original size
    const maxZoom = 3; // 300%
    const zoomStep = 0.25; // 25% increment

    // State for drag-to-scroll functionality
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [startY, setStartY] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [scrollTop, setScrollTop] = useState(0);

    // Refs
    const imageContainerRef = useRef(null);
    const imageRef = useRef(null);

    // Reset zoom when modal closes or image changes
    useEffect(() => {
        if (!isOpen) {
            setZoomLevel(1);
        }
    }, [isOpen, imageSrc]);

    // Zoom functions
    const handleZoomIn = () => {
        setZoomLevel(prev => Math.min(prev + zoomStep, maxZoom));
    };

    const handleZoomOut = () => {
        setZoomLevel(prev => Math.max(prev - zoomStep, minZoom));
    };

    const handleResetZoom = () => {
        setZoomLevel(1);
    };

    // Drag-to-scroll handlers
    const handleMouseDown = (e) => {
        if (zoomLevel <= 1 || !imageContainerRef.current) return;
        setIsDragging(true);
        setStartX(e.pageX - imageContainerRef.current.offsetLeft);
        setStartY(e.pageY - imageContainerRef.current.offsetTop);
        setScrollLeft(imageContainerRef.current.scrollLeft);
        setScrollTop(imageContainerRef.current.scrollTop);
    };

    const handleMouseMove = (e) => {
        if (!isDragging || !imageContainerRef.current) return;
        e.preventDefault();
        const x = e.pageX - imageContainerRef.current.offsetLeft;
        const y = e.pageY - imageContainerRef.current.offsetTop;
        const walkX = (x - startX) * 1.5;
        const walkY = (y - startY) * 1.5;
        imageContainerRef.current.scrollLeft = scrollLeft - walkX;
        imageContainerRef.current.scrollTop = scrollTop - walkY;
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleMouseLeave = () => {
        setIsDragging(false);
    };

    return (
        <ChakraProvider resetCSS={false}>
            <Modal isCentered isOpen={isOpen} onClose={onClose} size="full">
                <ModalOverlay bg="blackAlpha.800" />
                <ModalContent bg="transparent" boxShadow="none">
                    <ModalBody p={0} display="flex" alignItems="center" justifyContent="center" height="100vh" width="100vw">
                        {/* Outer wrapper with relative positioning for fixed buttons */}
                        <Box position="relative" display="flex" alignItems="center" justifyContent="center">
                            {/* Scrollable image container */}
                            <Box
                                ref={imageContainerRef}
                                overflow={zoomLevel > 1 ? 'auto' : 'visible'}
                                maxHeight="95vh"
                                maxWidth="95vw"
                                height="auto"
                                width="auto"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                cursor={zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'}
                                onMouseDown={handleMouseDown}
                                onMouseMove={handleMouseMove}
                                onMouseUp={handleMouseUp}
                                onMouseLeave={handleMouseLeave}
                                sx={{
                                    '&::-webkit-scrollbar': {
                                        width: '0px',
                                        height: '0px',
                                        background: 'transparent'
                                    },
                                    msOverflowStyle: 'none',
                                    scrollbarWidth: 'none'
                                }}
                            >
                                <Box
                                    style={{
                                        width: zoomLevel > 1 ? `${100 * zoomLevel}%` : 'auto',
                                        height: zoomLevel > 1 ? `${100 * zoomLevel}%` : 'auto',
                                        display: 'flex',
                                        alignItems: zoomLevel > 1 ? 'flex-start' : 'center',
                                        justifyContent: zoomLevel > 1 ? 'flex-start' : 'center',
                                    }}
                                >
                                    <img
                                        ref={imageRef}
                                        src={`data:image/png;base64,${imageSrc}`}
                                        alt="Full Size"
                                        style={{
                                            width: 'auto',
                                            height: 'auto',
                                            maxWidth: '95vw',
                                            maxHeight: '95vh',
                                            display: 'block',
                                            objectFit: 'contain',
                                            transform: `scale(${zoomLevel})`,
                                            transformOrigin: zoomLevel > 1 ? 'top left' : 'center center',
                                            pointerEvents: 'none',
                                            userSelect: 'none',
                                            transition: zoomLevel === 1 ? 'none' : 'transform 0.2s ease' // 🔥 prevents flicker when resetting to 100%
                                        }}
                                        draggable="false"
                                    />

                                </Box>
                            </Box>

                            {/* Close Button - Fixed position, outside scrollable area */}
                            {!hideZoomControls && (
                                <Box
                                    position="absolute"
                                    top="15px"
                                    right="15px"
                                    zIndex={10}
                                    pointerEvents="auto"
                                >
                                    <button
                                        onClick={onClose}
                                        style={{
                                            background: 'rgba(164, 164, 164, 0.7)',
                                            color: 'white',
                                            border: '2px solid black',
                                            borderRadius: '50%',
                                            width: '40px',
                                            height: '40px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <span style={{ fontSize: '24px', fontWeight: 'bold', background: 'transparent' }}>×</span>
                                    </button>
                                </Box>
                            )}

                            {/* Zoom Controls - Fixed position, outside scrollable area */}
                            {!hideZoomControls && (
                                <Box
                                    position="absolute"
                                    top="15px"
                                    right="65px"
                                    zIndex={10}
                                    display="flex"
                                    gap="4px"
                                    pointerEvents="auto"
                                >
                                    {/* Zoom Out Button */}
                                    <button
                                        onClick={handleZoomOut}
                                        disabled={zoomLevel <= minZoom}
                                        style={{
                                            background: 'rgba(164, 164, 164, 0.7)',
                                            color: 'white',
                                            border: '2px solid black',
                                            borderRadius: '50%',
                                            width: '40px',
                                            height: '40px',
                                            cursor: zoomLevel <= minZoom ? 'not-allowed' : 'pointer',
                                            opacity: zoomLevel <= minZoom ? 0.5 : 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (zoomLevel > minZoom) {
                                                e.target.style.background = 'rgba(164, 164, 164, 0.9)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.background = 'rgba(164, 164, 164, 0.7)';
                                        }}
                                    >
                                        <span style={{ fontSize: '24px', fontWeight: 'bold', background: 'transparent' }}>−</span>
                                    </button>

                                    {/* Reset/Show Zoom Percentage */}
                                    <button
                                        onClick={handleResetZoom}
                                        style={{
                                            background: 'rgba(164, 164, 164, 0.7)',
                                            color: 'white',
                                            border: '2px solid black',
                                            borderRadius: '20px',
                                            padding: '8px 12px',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            transition: 'all 0.2s',
                                            minWidth: '60px'
                                        }}
                                    >
                                        {Math.round(zoomLevel * 100)}%
                                    </button>

                                    {/* Zoom In Button */}
                                    <button
                                        onClick={handleZoomIn}
                                        disabled={zoomLevel >= maxZoom}
                                        style={{
                                            background: 'rgba(164, 164, 164, 0.7)',
                                            color: 'white',
                                            border: '2px solid black',
                                            borderRadius: '50%',
                                            width: '40px',
                                            height: '40px',
                                            cursor: zoomLevel >= maxZoom ? 'not-allowed' : 'pointer',
                                            opacity: zoomLevel >= maxZoom ? 0.5 : 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <span style={{ fontSize: '24px', fontWeight: 'bold', background: 'transparent' }}>+</span>
                                    </button>
                                </Box>
                            )}
                        </Box>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </ChakraProvider>
    );
}

export default ImageModal;